import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useDispatch } from "react-redux";
import { CreateDeliverableParams } from "../../../../api/services/programTemplate/models";
import {
  useAddExistingDeliverableMutation,
  useAddNewDeliverableMutation,
  useGetExistingDeliverablesById,
  useGetProgramTemplateDeliverableById,
  useUpdateDeliverableMutation,
} from "../../../../api/services/programTemplate/programTemplateApis";
import {
  FormController,
  MuiBox,
  MuiModal,
  MuiRadioGroup,
} from "../../../../components";
import {
  ADD_DELIVERABLE_TYPE,
  AddDeliverableType,
} from "../../../../helpers/constants";
import { UnitTypesKey } from "../../../../helpers/constants/siteCoreKeys";
import { isNotEmpty, replaceHashWithValue } from "../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../redux/slices/appState/appStateSlice";
import {
  CreateDeliverablesFormKey,
  CreateDeliverablesFormProps,
  ExistingDeliverableFilterFormProps,
  ExistingDeliverableFormKey,
  ExistingDeliverablesType,
} from "../../modelsAndSchemas/Models";
import { AddDeliverableValidationSchema } from "../../modelsAndSchemas/ValidationSchema";
import { createProgramSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { CancelFieldModal } from "../cancelFieldModal/CancelFieldModal";
import { CreateDeliverableWrapper } from "./CreateDeliverable.style";
import ExistingDeliverables from "./CreateDeliverableComponents/ExistingDeliverables/ExistingDeliverables";
import NewDeliverable from "./CreateDeliverableComponents/NewDeliverable/NewDeliverable";

interface CreateDeliverableProps {
  show: boolean;
  handleCloseModal: () => void;
  pageId: string;
  programTemplateId: string;
  getListOfDeliverablesApi: () => void;
  isEditDeliverable?: boolean;
  deliverableId?: string;
}

export const CreateDeliverable: FC<CreateDeliverableProps> = ({
  show,
  handleCloseModal,
  pageId,
  programTemplateId,
  getListOfDeliverablesApi,
  isEditDeliverable = false,
  deliverableId,
}) => {
  const dispatch = useDispatch();

  const [selectedUnit, setSelectedUnit] = useState<string>("");

  const [showCancel, setShowCancel] = useState(false);

  const [existingDeliverablesList, setExistingDeliverablesList] = useState<
    ExistingDeliverablesType[]
  >([]);

  // updated sitecore data
  const [allUpdatedRows, setAllUpdatedRows] = useState<
    ExistingDeliverablesType[]
  >([]);

  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);

  const tableRef = useRef<{ resetPagination: () => void }>(null);

  const [errorMessage, setErrorMessage] = useState<string>("");

  const [measurementShouldReset, setMeasurementShouldReset] = useState(true);

  const {
    commonLabels,
    fetchLabel,
    commonValidationMessages,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const {
    data: existingDeliverablesData,
    isLoading: existingDeliverablePending,
    isError: isExistingDeliverableError,
    isSuccess: isExistingDeliverableSuccess,
  } = useGetExistingDeliverablesById(programTemplateId);

  const {
    mutateAsync: addNewDeliverableApi,
    isPending: isAddNewPending,
    isError: isAddNewError,
    isSuccess: isAddNewSuccess,
  } = useAddNewDeliverableMutation();

  const {
    mutateAsync: addExistingDeliverableApi,
    isPending: isAddExistingPending,
    isError: isAddExistingError,
    isSuccess: isAddExistingSuccess,
  } = useAddExistingDeliverableMutation();

  const {
    mutateAsync: updateDeliverableApi,
    isPending: isUpdatePending,
    isError: isUpdateError,
    isSuccess: isUpdateSuccess,
  } = useUpdateDeliverableMutation();

  const {
    data: deliverableData,
    isPending: isPendingDeliverable,
    isError: isErrorDeliverable,
    isSuccess: isSuccessDeliverable,
  } = useGetProgramTemplateDeliverableById(
    programTemplateId,
    deliverableId ?? "",
  );

  const activityTypes = useMemo(() => {
    return Array.isArray(deliverableData?.activityTypes)
      ? deliverableData?.activityTypes.map((data: any) => ({
          value: data.id,
          label: data.name,
        }))
      : [];
  }, [deliverableData]);

  useEffect(() => {
    if (isExistingDeliverableSuccess) {
      resetExistingDeliverables();
    }
  }, [existingDeliverablesData]);

  const resetExistingDeliverables = () => {
    if (existingDeliverablesData) {
      const myData = [...existingDeliverablesData];

      myData.forEach((data) => {
        data.unitName = fetchValueByKeyOfDropdown(
          createProgramSiteCoreKeys.createDeliverableKey,
          createProgramSiteCoreKeys.createDeliverableUnitList,
          data.units,
        );
        data.deliverableTypeName = fetchValueByKeyOfDropdown(
          createProgramSiteCoreKeys.createDeliverableKey,
          createProgramSiteCoreKeys.deliverableTypeList,
          data.deliverableType,
        );
        data.measurement =
          data.units === UnitTypesKey.Minutes
            ? Number(
                fetchValueByKeyOfDropdown(
                  createProgramSiteCoreKeys.createDeliverableKey,
                  createProgramSiteCoreKeys.createDeliverableMinuteList,
                  data.measurement,
                ),
              )
            : Number(data.measurement);
      });
      setExistingDeliverablesList([...myData]);
      setAllUpdatedRows([...myData]);
      // Check if all rows have isSelected as true
      const allSelected = myData.every((row) => row.isSelected);
      setIsAllSelected(allSelected);

      resetExistingFilter();
      resetPagination();
    }
  };

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible:
          isAddNewPending ||
          isAddExistingPending ||
          isPendingDeliverable ||
          isUpdatePending,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [
    isAddNewPending,
    isAddExistingPending,
    isPendingDeliverable,
    isUpdatePending,
  ]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: existingDeliverablePending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [existingDeliverablePending]);

  useEffect(() => {
    if (isAddExistingSuccess || isAddNewSuccess || isUpdateSuccess) {
      dispatch(
        toggleToast({
          open: true,
          message: fetchLabel(
            createProgramSiteCoreKeys.createDeliverableKey,
            isEditDeliverable && isUpdateSuccess
              ? createProgramSiteCoreKeys.deliverableUpdatedMessage
              : createProgramSiteCoreKeys.deliverableAddedMessage,
          ),
        }),
      );
      getListOfDeliverablesApi();
      handleCloseModal();
    }
    if (
      isAddNewError ||
      isAddExistingError ||
      isExistingDeliverableError ||
      isErrorDeliverable ||
      isUpdateError
    ) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [
    isAddNewSuccess,
    isAddNewError,
    isAddExistingError,
    isAddExistingSuccess,
    isExistingDeliverableError,
    isErrorDeliverable,
    isUpdateError,
    isUpdateSuccess,
  ]);

  const defaultValues = {
    [CreateDeliverablesFormKey.addDeliverableType]:
      ADD_DELIVERABLE_TYPE.newKey as AddDeliverableType,
    [CreateDeliverablesFormKey.deliverableType]: "",
    [CreateDeliverablesFormKey.name]: "",
    [CreateDeliverablesFormKey.units]: "",
    [CreateDeliverablesFormKey.measurementMinute]: "",
    [CreateDeliverablesFormKey.measurementOther]: null,
  };

  const {
    handleSubmit: handleSubmitForm,
    control,
    getValues,
    setValue,
    formState: { isDirty: isDirtyForm, touchedFields },
    reset,
    clearErrors,
  } = useForm<CreateDeliverablesFormProps>({
    mode: "all",
    defaultValues: defaultValues,
    resolver: yupResolver(
      AddDeliverableValidationSchema(commonValidationMessages) as any,
    ),
  });

  useEffect(() => {
    if (isSuccessDeliverable && deliverableData) {
      setSelectedUnit(deliverableData.units);
      reset({
        ...defaultValues,
        [CreateDeliverablesFormKey.name]: deliverableData.name,
        [CreateDeliverablesFormKey.deliverableType]:
          deliverableData.deliverableType,
        [CreateDeliverablesFormKey.units]: deliverableData.units,
        ...(deliverableData.units === UnitTypesKey.Minutes
          ? {
              [CreateDeliverablesFormKey.measurementMinute]:
                deliverableData.measurement,
            }
          : {
              [CreateDeliverablesFormKey.measurementOther]:
                deliverableData.measurement,
            }),
      });
      setMeasurementShouldReset(false);
    }
  }, [isSuccessDeliverable, deliverableData]);

  const deliverableTypeWatch = useWatch({
    name: CreateDeliverablesFormKey.addDeliverableType,
    control,
  });

  useEffect(() => {
    if (touchedFields.addDeliverableType) {
      if (getValues().addDeliverableType === ADD_DELIVERABLE_TYPE.newKey) {
        // need to check update scenario
        reset({
          ...defaultValues,
          [CreateDeliverablesFormKey.addDeliverableType]:
            getValues().addDeliverableType,
        });
        setSelectedUnit("");
      } else {
        resetExistingDeliverables();
      }
    }
  }, [deliverableTypeWatch]);

  const unitsWatch = useWatch({
    name: CreateDeliverablesFormKey.units,
    control,
  });

  useEffect(() => {
    if (measurementShouldReset && touchedFields.units) {
      clearErrors([
        CreateDeliverablesFormKey.measurementMinute,
        CreateDeliverablesFormKey.measurementOther,
      ]);
      setValue(CreateDeliverablesFormKey.measurementMinute, "");
      setValue(CreateDeliverablesFormKey.measurementOther, null);
    }
    setMeasurementShouldReset(true);
  }, [unitsWatch]);

  const onFinalSubmitClick = () => {
    if (getValues().addDeliverableType === ADD_DELIVERABLE_TYPE.newKey) {
      const {
        name,
        deliverableType,
        units,
        measurementMinute,
        measurementOther,
      } = getValues();

      const getMeasurement = () => {
        switch (units) {
          case UnitTypesKey.Minutes:
            return measurementMinute;
          case UnitTypesKey.Count:
          case UnitTypesKey.Days:
            return (measurementOther as number).toString();
          default:
            return "1";
        }
      };

      const params: CreateDeliverableParams = {
        name,
        deliverableType,
        units,
        measurement: getMeasurement(),
        localizedDeliverableNames: [],
      };

      if (isEditDeliverable) {
        if (deliverableId) {
          updateDeliverableApi({ params, programTemplateId, deliverableId });
        }
      } else {
        addNewDeliverableApi({ params, programTemplateId });
      }
    } else {
      //existingKey
      const selectedDeliverablesIds: string[] = existingDeliverablesList
        .filter((row) => row.isSelected)
        .map((item) => item.id);

      if (isNotEmpty(selectedDeliverablesIds)) {
        const params = {
          deliverableIds: selectedDeliverablesIds,
        };
        addExistingDeliverableApi({
          params,
          programTemplateId,
        });
      } else {
        setErrorMessage(
          fetchLabel(
            createProgramSiteCoreKeys.createDeliverableKey,
            createProgramSiteCoreKeys.deliverableSaveAlertMessage,
          ),
        );
      }
    }
  };

  const onConfirmClick = () => {
    handleSubmitForm(onFinalSubmitClick)();
  };

  const closeModalHandler = () => setShowCancel(false);

  const cancelMainModalHandler = () => {
    if (isDirtyForm) setShowCancel(true);
    else handleCloseModal();
  };

  const cancelConfirmHandler = () => {
    setShowCancel(false);
    handleCloseModal();
  };

  const deliverablesTitle = () => {
    if (isEditDeliverable) {
      return replaceHashWithValue(
        fetchLabel(
          createProgramSiteCoreKeys.deliverablesListKey,
          createProgramSiteCoreKeys.editDeliverableTitle,
        ),
        deliverableData?.name ?? "",
      );
    }

    return fetchLabel(
      createProgramSiteCoreKeys.createDeliverableKey,
      createProgramSiteCoreKeys.addDeliverable,
    );
  };

  const radioOptions = [
    {
      value: ADD_DELIVERABLE_TYPE.newKey,
      label: fetchLabel(
        createProgramSiteCoreKeys.createDeliverableKey,
        createProgramSiteCoreKeys.addNewDeliverable,
      ),
    },
    {
      value: ADD_DELIVERABLE_TYPE.existingKey,
      label: fetchLabel(
        createProgramSiteCoreKeys.createDeliverableKey,
        createProgramSiteCoreKeys.selectExistingDeliverables,
      ),
    },
  ];

  const handleSearchTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(
      CreateDeliverablesFormKey.addDeliverableType,
      e.target.value as AddDeliverableType,
    );
    setErrorMessage("");
  };

  const handleUnitChange = (value: string) => {
    setSelectedUnit(value);
  };

  const onHeaderCheckBoxChange = (checked: boolean) => {
    setIsAllSelected(checked);
    const updatedRows = existingDeliverablesList.map((row) => ({
      ...row,
      isSelected: checked,
    }));
    setExistingDeliverablesList(updatedRows);

    // Also update `allUpdatedRows` to reflect changes for the whole dataset
    const updatedAllRows = allUpdatedRows.map((row) => {
      // Find the matching row in `existingDeliverablesList` and update it
      const matchingRow = updatedRows.find(
        (updatedRow) => updatedRow.id === row.id,
      );
      return matchingRow ? { ...row, isSelected: matchingRow.isSelected } : row;
    });

    // Update `allUpdatedRows` state
    setAllUpdatedRows(updatedAllRows);

    setErrorMessage("");
  };

  const handleRowCheckboxChange = (checked: boolean, rowId: string) => {
    // Update only the clicked row's `isSelected` value
    const updatedRows = existingDeliverablesList.map((row) =>
      row.id === rowId ? { ...row, isSelected: checked } : row,
    );
    setExistingDeliverablesList(updatedRows);

    // Check if all rows have isSelected as true
    const allSelected = updatedRows.every((row) => row.isSelected);
    setIsAllSelected(allSelected);

    const updatedAllRows = allUpdatedRows.map((row) =>
      row.id === rowId ? { ...row, isSelected: checked } : row,
    );
    setAllUpdatedRows(updatedAllRows);

    setErrorMessage("");
  };

  const {
    control: existingFilterFormControl,
    watch: existingWatch,
    reset: resetExistingFilter,
    getValues: getExistingFilterValues,
  } = useForm<ExistingDeliverableFilterFormProps>({
    mode: "all",
    defaultValues: {
      [ExistingDeliverableFormKey.deliverableType]: "",
      [ExistingDeliverableFormKey.name]: "",
      [ExistingDeliverableFormKey.units]: "",
    },
  });

  const applyFilters = (data: ExistingDeliverablesType[]) => {
    const filters = getExistingFilterValues();

    return data.filter((item) => {
      return Object.keys(filters).every((key) => {
        const filterValue = filters[key as keyof typeof filters];

        const filterValueStr = filterValue?.toString().toLowerCase();

        if (item[key] === undefined && filterValue !== "") {
          return false;
        }

        if (filterValue === "") return true;

        const itemValue = item[key]?.toString().toLowerCase();

        return itemValue?.includes(filterValueStr);
      });
    });
  };

  useEffect(() => {
    if (isNotEmpty(allUpdatedRows)) {
      const filteredData = applyFilters(allUpdatedRows);
      setExistingDeliverablesList(filteredData);
      resetPagination();
    }
  }, [
    existingWatch(ExistingDeliverableFormKey.name),
    existingWatch(ExistingDeliverableFormKey.deliverableType),
    existingWatch(ExistingDeliverableFormKey.units),
  ]);

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  return (
    <MuiModal
      open={show}
      onClose={cancelMainModalHandler}
      title={deliverablesTitle()}
      primaryButtonTitle={
        isEditDeliverable ? commonLabels.update : commonLabels.save
      }
      secondaryButtonTitle={commonLabels.cancel}
      onPrimaryButtonClick={onConfirmClick}
      onSecondaryButtonClick={cancelMainModalHandler}
      hasLargeContent
      footerErrorMessage={errorMessage}
    >
      <CreateDeliverableWrapper data-testid={`${pageId}-add-deliverable`}>
        {!isEditDeliverable && (
          <MuiBox className="radio-container">
            <FormController
              control={control}
              name={CreateDeliverablesFormKey.addDeliverableType}
            >
              <MuiRadioGroup
                label={fetchLabel(
                  createProgramSiteCoreKeys.createDeliverableKey,
                  createProgramSiteCoreKeys.selectType,
                )}
                value={
                  getValues(CreateDeliverablesFormKey.addDeliverableType) ?? ""
                }
                onChange={handleSearchTypeChange}
                options={radioOptions}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiBox>
        )}

        {getValues().addDeliverableType === ADD_DELIVERABLE_TYPE.newKey && (
          <NewDeliverable
            pageId={pageId}
            control={control}
            selectedUnit={selectedUnit}
            handleUnitChange={handleUnitChange}
            isEditDeliverable={isEditDeliverable}
            activityTypes={activityTypes}
          />
        )}

        {getValues().addDeliverableType ===
          ADD_DELIVERABLE_TYPE.existingKey && (
          <ExistingDeliverables
            onHeaderCheckBoxChange={onHeaderCheckBoxChange}
            isAllSelected={isAllSelected}
            pageId={pageId}
            handleRowCheckboxChange={handleRowCheckboxChange}
            existingFilterFormControl={existingFilterFormControl}
            tableRef={tableRef}
            existingDeliverablesList={existingDeliverablesList}
            noDataInApi={allUpdatedRows.length === 0}
          />
        )}

        {showCancel && (
          <CancelFieldModal
            showModal={showCancel}
            handleModalClose={closeModalHandler}
            handlePrimaryButtonClick={cancelConfirmHandler}
          />
        )}
      </CreateDeliverableWrapper>
    </MuiModal>
  );
};
